import { DealsheetService } from '@services/dealsheet/DealsheetService';
import { DismissAllToasts, ErrorToast } from '@services/utils/ToastUtils';
import { useDispatch, useSelector } from 'react-redux';
import { PortfolioService } from '../services/portfolio/PortfolioService';
import { AppState } from '../store/AppState';
import {
  setIsPortfolioBulkExportInPorgress,
  setIsPortfolioBulkPrintInPorgress,
  setIsPortfolioLoading,
  setPortfolioShouldRefresh,
  setPortfolioShouldRefreshDealCount,
} from '../store/portfolio/PortfolioActions';
import { PortfolioState } from '../store/portfolio/PortfolioReducer';
import { FilterItemResource } from '../types/FilterTypes';
import {
  BulkExportListResource,
  BulkPrintRequestResource,
  BulkPrintResource,
  BulkReassignDealsResource,
  BulkUpdateMarkInactiveResource,
  BulkUpdateReplacementInfoResource,
  BulkUpdateSendLeadsToCRMResource,
  DealerClosedDealResource,
  MailerStatusRequestResource,
  ReassignDealsQueryResource,
  ReassignDealsResource,
} from '../types/PortfolioBulkActionResource';
import { PortfolioRequestResource } from '../types/PortfolioRequestResource';
import { PortfolioResource } from '../types/PortfolioResource';
import { PortfolioSalesPerson } from '../types/SalesPerson';
import { usePortfolioFilter } from './UsePortfolioFilter';

export const usePortfolioBulkActionsContainer = (): PortfolioBulkActionsContainerType => {
  const { dealType } = useSelector<AppState, PortfolioState>((state) => state.portfolio);
  const { getPortfolioView } = usePortfolioFilter();
  const dispatch = useDispatch();

  const createReassignPayload = (
    assignees: PortfolioSalesPerson[],
    selectedAssignees: FilterItemResource[],
    isAllRowsSelected: boolean,
    isClearSelectionLink: boolean,
    selectedRecords: PortfolioResource[],
  ): ReassignDealsResource => {
    const currentView = getPortfolioView(dealType);
    const portfolioRequest = currentView.portfolioRequest;
    let isAllRecordsUpdate: boolean = false;

    if (isAllRowsSelected && isClearSelectionLink) {
      isAllRecordsUpdate = true;
    }

    const bulkReassignDealsResourceList: Array<BulkReassignDealsResource> = [];
    const selectedSalesPersons: Array<PortfolioSalesPerson> = [];

    const dealerIds = selectedRecords
      .map((item) => item.dealerId)
      .filter((value, index, self) => self.indexOf(value) === index);

    selectedAssignees.forEach((selectedAssignee) => {
      const salesPerson = assignees.find(
        (x) => x.name.toLowerCase() === selectedAssignee.key.toLowerCase(),
      );
      selectedSalesPersons.push(salesPerson);
    });

    dealerIds.forEach((dealerId) => {
      const closeDealIds = selectedRecords
        .filter((x) => x.dealerId === dealerId)
        .map((x) => x.closedDealId);
      const vins = selectedRecords
        .filter((x) => x.dealerId === dealerId)
        .map((x) => x.replacementDeal.vin);
      const reassignDeals: ReassignDealsQueryResource = {
        closedDealIds: closeDealIds,
        vins: vins,
      };

      bulkReassignDealsResourceList.push({
        dealerId: dealerId,
        reassignDeals: reassignDeals,
      });
    });

    const result: ReassignDealsResource = {
      portfolioRequestResource: !isAllRecordsUpdate
        ? portfolioRequest
        : {
            ...portfolioRequest,
            sort: '',
            sortDirection: '',
            currentPage: 0,
            recordsPerPage: 0,
          },
      bulkReassignDealsResource: !isAllRecordsUpdate ? bulkReassignDealsResourceList : [],
      selectedAssignees: selectedSalesPersons,
    };
    return result;
  };

  const bulkReassignDeals = (payload: ReassignDealsResource, successCallBack: any): void => {
    dispatch(setIsPortfolioLoading(true));
    PortfolioService.reassignDeals(payload)
      .then(() => {
        successCallBack();
        dispatch(setIsPortfolioLoading(false));
        dispatch(setPortfolioShouldRefresh(true, dealType));
      })
      .catch((err) => {
        dispatch(setIsPortfolioLoading(false));
        console.log('Error while reassigning deals', err);
        ErrorToast('Error occured while reassigning deals');
      });
  };

  const createMarkInactivePayload = (
    isAllRowsSelected: boolean,
    isClearSelectionLink: boolean,
    selectedRecords: PortfolioResource[],
  ): BulkUpdateMarkInactiveResource => {
    const currentView = getPortfolioView(dealType);
    const portfolioRequest = currentView.portfolioRequest;
    let isAllRecordsUpdate: boolean = false;
    if (isAllRowsSelected && isClearSelectionLink) {
      isAllRecordsUpdate = true;
    }
    const bulkUpdateDealerClosedDealResourceList: Array<DealerClosedDealResource> = [];

    const dealerIds = selectedRecords
      .map((item) => item.dealerId)
      .filter((value, index, self) => self.indexOf(value) === index);
    dealerIds.forEach((dealerId) => {
      const closeDealIds = selectedRecords
        .filter((x) => x.dealerId === dealerId)
        .map((x) => x.closedDealId);
      bulkUpdateDealerClosedDealResourceList.push({
        dealerId: dealerId,
        closedDealIds: closeDealIds,
      });
    });

    const result: BulkUpdateMarkInactiveResource = {
      portfolioRequestResource: !isAllRecordsUpdate
        ? portfolioRequest
        : {
            ...portfolioRequest,
            sort: '',
            sortDirection: '',
            currentPage: 0,
            recordsPerPage: 0,
          },
      dealerClosedDealResource: !isAllRecordsUpdate ? bulkUpdateDealerClosedDealResourceList : [],
    };
    return result;
  };

  const bulkMarkInactiveDeals = (
    payload: BulkUpdateMarkInactiveResource,
    successCallBack: any,
  ): void => {
    dispatch(setIsPortfolioLoading(true));
    PortfolioService.markInactiveDeals(payload)
      .then(() => {
        successCallBack();
        dispatch(setIsPortfolioLoading(false));
        dispatch(setPortfolioShouldRefreshDealCount(true, dealType));
        dispatch(setPortfolioShouldRefresh(true, dealType));
      })
      .catch((err) => {
        dispatch(setIsPortfolioLoading(false));
        console.log('Error while marking inactive deals', err);
        ErrorToast('Error occured while marking inactive deals');
      });
  };

  const createMailerStatusPayload = (
    isAllRowsSelected: boolean,
    isClearSelectionLink: boolean,
    selectedRecords: PortfolioResource[],
    dateToSet: Date,
    isPostMail: boolean,
  ): MailerStatusRequestResource => {
    const currentView = getPortfolioView(dealType);
    const portfolioRequest = currentView.portfolioRequest;
    let isAllRecordsUpdate: boolean = false;

    if (isAllRowsSelected && isClearSelectionLink) {
      isAllRecordsUpdate = true;
    }

    const dealerClosedDealList: Array<DealerClosedDealResource> = [];

    const dealerIds = selectedRecords
      .map((item) => item.dealerId)
      .filter((value, index, self) => self.indexOf(value) === index);

    dealerIds.forEach((dealerId) => {
      const closeDealIds = selectedRecords
        .filter((x) => x.dealerId === dealerId)
        .map((x) => x.closedDealId);

      const dealerClosedDeal: DealerClosedDealResource = {
        dealerId: dealerId,
        closedDealIds: closeDealIds,
      };

      dealerClosedDealList.push(dealerClosedDeal);
    });

    const result: MailerStatusRequestResource = {
      portfolioRequestResource: !isAllRecordsUpdate
        ? portfolioRequest
        : {
            ...portfolioRequest,
            sort: '',
            sortDirection: '',
            currentPage: 0,
            recordsPerPage: 0,
          },
      dealerClosedDealResources: !isAllRecordsUpdate ? dealerClosedDealList : [],
      dateToSet: dateToSet,
      isPostMail: isPostMail,
    };

    return result;
  };

  const bulkUpdateMailerStatus = (
    payload: MailerStatusRequestResource,
    successCallBack: any,
  ): void => {
    dispatch(setIsPortfolioLoading(true));
    PortfolioService.bulkChangeMailers(payload)
      .then(() => {
        successCallBack();
        dispatch(setIsPortfolioLoading(false));
        dispatch(setPortfolioShouldRefreshDealCount(true, dealType));
        dispatch(setPortfolioShouldRefresh(true, dealType));
      })
      .catch((err) => {
        dispatch(setIsPortfolioLoading(false));
        console.log('Error updating mailer status', err);
        ErrorToast('Error occured updating mailer status');
      });
  };

  const createUpdateReplacementPayload = (): PortfolioRequestResource => {
    const currentView = getPortfolioView(dealType);
    const portfolioRequest = currentView.portfolioRequest;
    return {
      ...portfolioRequest,
      sort: '',
      sortDirection: '',
      currentPage: 0,
      recordsPerPage: 0,
    };
  };

  const dealAggregationsForUpdateReplacement = async (
    payload: PortfolioRequestResource,
  ): Promise<Array<BulkUpdateReplacementInfoResource>> => {
    let result: Array<BulkUpdateReplacementInfoResource>;
    dispatch(setIsPortfolioLoading(true));
    await PortfolioService.fetchBulkUpdateReplacementData(payload)
      .then((response) => {
        result = response.map((row) => {
          return {
            dealsCount: row.dealsCount,
            dealerId: row.dealerId,
            dealType: row.dealType,
          };
        });
      })
      .catch((err) => {
        console.log('Error during fetch', err);
        ErrorToast('Error occured during fetch of bulk update replacement deals');
      })
      .finally(() => {
        dispatch(setIsPortfolioLoading(false));
      });

    return result;
  };

  const createSendLeadsToCRMPayload = (
    isAllRowsSelected: boolean,
    isClearSelectionLink: boolean,
    selectedRecords: PortfolioResource[],
  ): BulkUpdateSendLeadsToCRMResource => {
    const currentView = getPortfolioView(dealType);
    const { portfolioRequest } = currentView;
    const isAllRecordsUpdate: boolean = isAllRowsSelected && isClearSelectionLink;
    const sendLeadsToCRMResourceList: DealerClosedDealResource[] = [];

    if (!isAllRecordsUpdate) {
      // If total records do not select to process, then only create the payload for the selected records only and push them to the sendLeadsToCRMResourceList.
      selectedRecords.forEach((record) => {
        // Find the record index on an object for the DealerId.
        const recordIndex = sendLeadsToCRMResourceList.findIndex(
          (rec) => rec.dealerId === record.dealerId,
        );

        // If an object for the same DealerId does not exists in the list then create and push the new object with the ClosedDealIds.
        if (recordIndex === -1) {
          sendLeadsToCRMResourceList.push({
            dealerId: record.dealerId,
            closedDealIds: [record.closedDealId],
          });
        } // Push the ClosedDealIds to that object that exists on the founded recordIndex.
        else {
          sendLeadsToCRMResourceList[recordIndex].closedDealIds.push(record.closedDealId);
        }
      });
    }
    const result: BulkUpdateSendLeadsToCRMResource = {
      portfolioRequestResource: !isAllRecordsUpdate
        ? portfolioRequest
        : {
            ...portfolioRequest,
            sort: '',
            sortDirection: '',
            currentPage: 0,
            recordsPerPage: 0,
          },
      sendLeadsToCRMResources: sendLeadsToCRMResourceList,
    };
    return result;
  };

  const bulkSendLeadsToCRM = (
    payload: BulkUpdateSendLeadsToCRMResource,
    successCallBack: any,
  ): void => {
    dispatch(setIsPortfolioLoading(true));
    PortfolioService.sendLeadsToCRM(payload)
      .then(() => {
        successCallBack();
      })
      .catch((error) => {
        console.log('Error while sending leads to CRM', error);
        if (error.message === '503') {
          ErrorToast('Please try again. An error occured during processing your request.');
        } else {
          ErrorToast(`Couldn't push the data.`);
        }
      })
      .finally(() => {
        dispatch(setIsPortfolioLoading(false));
      });
  };

  const createBulkPrintPayload = (
    isAllRowsSelected: boolean,
    isClearSelectionLink: boolean,
    selectedRecords: PortfolioResource[],
    maxNoOfPrintOffersToExport: number,
  ): BulkPrintRequestResource => {
    const currentView = getPortfolioView(dealType);
    const portfolioRequest = currentView.portfolioRequest;
    let isAllRecordsUpdate: boolean = false;

    if (isAllRowsSelected && isClearSelectionLink) {
      isAllRecordsUpdate = true;
    }

    const dealerVinCustomerNameList: Array<BulkPrintResource> = [];

    selectedRecords.slice(0, maxNoOfPrintOffersToExport)?.forEach((item) => {
      const dealerIdVinCustomerName: BulkPrintResource = {
        dealerId: item.dealerId,
        vin: item.replacementDeal.vin,
        customerName: item.customer.fullName,
      };

      dealerVinCustomerNameList.push(dealerIdVinCustomerName);
    });

    const result: BulkPrintRequestResource = {
      portfolioRequestResource: !isAllRecordsUpdate
        ? portfolioRequest
        : { ...portfolioRequest, currentPage: 0, recordsPerPage: 0 },
      deals: !isAllRecordsUpdate ? dealerVinCustomerNameList : [],
      notifCategory: null,
    };

    return result;
  };

  const bulkPrintOffer = (payload: BulkPrintRequestResource, successCallBack: any): void => {
    dispatch(setIsPortfolioBulkPrintInPorgress(true));

    DealsheetService.getBulkPrintOfferData(payload)
      .then((response) => {
        successCallBack(response);
      })
      .catch((err) => {
        DismissAllToasts();
        localStorage.setItem('portfolioBulkPrint', null);
        ErrorToast(
          'There was an error in producing the deal sheets you requested. Please try again.',
          false,
        );
        dispatch(setIsPortfolioBulkPrintInPorgress(false));
      });
  };

  const createBulkExportPayload = (
    isAllRowsSelected: boolean,
    isClearSelectionLink: boolean,
    selectedRecords: PortfolioResource[],
  ): BulkExportListResource => {
    const currentView = getPortfolioView(dealType);
    const portfolioRequest = currentView.portfolioRequest;
    let isAllRecordsUpdate: boolean = false;

    if (isAllRowsSelected && isClearSelectionLink) {
      isAllRecordsUpdate = true;
    }

    const closedDealIds = selectedRecords.map((x) => x.closedDealId);
    const result: BulkExportListResource = {
      ...portfolioRequest,
      closedDealIds: !isAllRecordsUpdate ? closedDealIds : [],
    };

    return result;
  };

  const downloadFile = (file: Blob, filename: string) => {
    const downloadUrl = window.URL.createObjectURL(new Blob([file]));
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', `${filename}`);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  };

  const getFilename = (headers: Headers): string => {
    return headers.get('Content-Disposition').split('filename=')[1].split(';')[0];
  };

  const bulkExportList = (payload: BulkExportListResource, successCallBack: any): void => {
    dispatch(setIsPortfolioBulkExportInPorgress(true));
    PortfolioService.bulkExportList(payload)
      .then(async (response) => {
        successCallBack();
        if (response.ok) {
          downloadFile(await response.blob(), getFilename(response.headers));
        } else {
          ErrorToast(`There was an error in exporting the data. Please try again.`);
        }
      })
      .catch((err) => ErrorToast(`There was an error in exporting the data. Please try again.`))
      .finally(() => {
        dispatch(setIsPortfolioBulkExportInPorgress(false));
      });
  };

  return {
    bulkReassignDeals,
    createReassignPayload,
    createMailerStatusPayload,
    bulkUpdateMailerStatus,
    createMarkInactivePayload,
    bulkMarkInactiveDeals,
    createUpdateReplacementPayload,
    dealAggregationsForUpdateReplacement,
    createSendLeadsToCRMPayload,
    bulkSendLeadsToCRM,
    createBulkPrintPayload,
    bulkPrintOffer,
    createBulkExportPayload,
    bulkExportList,
  };
};

type PortfolioBulkActionsContainerType = {
  bulkReassignDeals: (payload: ReassignDealsResource, successCallBack: any) => void;
  createReassignPayload: (
    assignees: PortfolioSalesPerson[],
    selectedAssignees: FilterItemResource[],
    isAllRowsSelected: boolean,
    isClearSelectionLink: boolean,
    selectedRecords: PortfolioResource[],
  ) => ReassignDealsResource;
  createMailerStatusPayload: (
    isAllRowsSelected: boolean,
    isClearSelectionLink: boolean,
    selectedRecords: PortfolioResource[],
    dateToSet: Date,
    isPostMail: boolean,
  ) => MailerStatusRequestResource;
  bulkUpdateMailerStatus: (payload: MailerStatusRequestResource, successCallBack: any) => void;
  bulkMarkInactiveDeals: (payload: BulkUpdateMarkInactiveResource, successCallBack: any) => void;
  createMarkInactivePayload: (
    isAllRowsSelected: boolean,
    isClearSelectionLink: boolean,
    selectedRecords: PortfolioResource[],
  ) => BulkUpdateMarkInactiveResource;
  createUpdateReplacementPayload: () => PortfolioRequestResource;
  dealAggregationsForUpdateReplacement: (
    payload: PortfolioRequestResource,
  ) => Promise<Array<BulkUpdateReplacementInfoResource>>;
  createSendLeadsToCRMPayload: (
    isAllRowsSelected: boolean,
    isClearSelectionLink: boolean,
    selectedRecords: PortfolioResource[],
  ) => BulkUpdateSendLeadsToCRMResource;
  bulkSendLeadsToCRM: (payload: BulkUpdateSendLeadsToCRMResource, successCallBack: any) => void;
  createBulkPrintPayload: (
    isAllRowsSelected: boolean,
    isClearSelectionLink: boolean,
    selectedRecords: PortfolioResource[],
    maxNoOfPrintOffersToExport: number,
  ) => BulkPrintRequestResource;
  bulkPrintOffer: (payload: BulkPrintRequestResource, successCallBack: any) => void;
  createBulkExportPayload: (
    isAllRowsSelected: boolean,
    isClearSelectionLink: boolean,
    selectedRecords: PortfolioResource[],
  ) => BulkExportListResource;
  bulkExportList: (payload: BulkExportListResource, successCallBack: any) => void;
};
